import React, { type ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Menu,
  MenuItem,
} from '@saviynt/design-system';

import { TABS_FILTERS } from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { globalActions } from '../../../../../shared/store/globalSlice';

import './ViolationsTabDetailsFooter.css';

/**
 * ViolationsTabDetailsFooter component renders the footer section of the Violations Tab Details panel.
 * It provides navigation controls and action buttons for violations management.
 *
 * The footer includes:
 * - A back button that returns to the previous screen or violations home based on source
 * - Conditional action buttons for mitigating and remediating violations
 * - A more options menu button
 */

interface ViolationsTabDetailsFooterProps {
  handleBackButton: () => void;
}

/**
 * Renders footer actions for violations details
 */
function ViolationsTabDetailsFooter({
  handleBackButton,
}: ViolationsTabDetailsFooterProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const {
    aag: {
      violationsTab: {
        controls: { tabInAction },
      },
    },
  } = useTypedSelector((state) => state);

  return (
    <>
      <Button kind='subtle' onClick={handleBackButton}>
        {l('Back')}
      </Button>
      <Box className='Sod-ViolationsTabDetailsFooter-actions'>
        {!(tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value) && (
          <Button
            isDisabled
            kind='outlined'
            leftIcon={<Icon kind='SodMitigate' />}>
            {l('Mitigate')}
          </Button>
        )}
        <Button
          isDisabled
          kind='outlined'
          leftIcon={<Icon kind='SodRemediate' />}>
          {l('Remediate')}
        </Button>
        {/* IS_READ_ONLY */}
        <Menu
          CustomMenu={
            <Box
              tag='div'
              className='Sod-ViolationsListActions--isReadOnlyWrapper'>
              <MenuItem
                option={{
                  label: l('Return to Classic Experience to Take Actions'),
                  secondaryLabel: l(
                    "The new experience doesn't support actions yet."
                  ),
                  value: 'return_to_classic',
                }}
                onClick={() => {
                  dispatch(globalActions.setIsClassisExperience(true));
                }}
                className='Sod-ViolationsListActions--isReturnToClassicBtn'
              />
            </Box>
          }
          trigger={
            <ButtonIcon
              size='medium'
              kind='ghost'
              icon={
                <Icon kind='MoreVertical' color='neutral-100' size='medium' />
              }
            />
          }
        />
      </Box>
    </>
  );
}

export default ViolationsTabDetailsFooter;
